import React from "react";
import { useParams } from "react-router-dom";
import PlaceholderPage from "./PlaceholderPage";

// Legal placeholder pages — TOS, Comments Policy, DMCA. Each renders with
// stubbed but sensible text so the footer links resolve; final legal language
// will be reviewed and replaced by counsel.
const CONTENT = {
  terms: {
    eyebrow: "Legal",
    title: "Terms of Service",
    body: (
      <>
        <p>
          Welcome to Vexent Wire. By accessing or using this website you agree to these Terms of Service.
          If you do not agree, please do not use the site.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          1. Editorial nature of content
        </p>
        <p>
          Vexent Wire publishes independent research, market analysis, and editorial commentary. Content is
          provided for informational and educational purposes only. Nothing on this site is investment, legal,
          tax, or accounting advice, and nothing constitutes a solicitation to buy or sell any security or asset.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          2. Free access and reader accounts
        </p>
        <p>
          The daily feed, market commentary, and most articles are available to read without an account or
          payment. Some interactive features (commenting, newsletter delivery, saved articles) require a free
          reader account.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          3. Paid subscriptions (Premium &amp; Professional)
        </p>
        <p>
          Premium and Professional plans include a 30-day free trial. A valid payment method is required at
          signup. If you do not cancel before the end of the trial, your card will be automatically charged
          at the plan's monthly rate (or annual rate if selected) and will continue to be charged on each
          subsequent renewal date until you cancel. Subscription pricing is displayed clearly on the pricing
          page before signup.
        </p>
        <p>
          You may cancel at any time from your account settings or via the Stripe-hosted billing portal.
          Cancellation stops all future charges; access continues through the end of the current paid
          billing period. See our{" "}
          <a href="/legal/billing" className="underline hover:text-[#0891B2]">Billing &amp; Refunds</a>
          {" "}policy for the full auto-renewal and cancellation terms.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          4. Comments and user submissions
        </p>
        <p>
          Reader comments are welcome but subject to the Comment Policy. Vexent Wire reserves the right to
          moderate, edit, or remove any comment at its sole discretion.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          5. Intellectual property
        </p>
        <p>
          All content on this site — text, images, audio narration, layout, and design — is the property of
          Vexent Wire and its licensors. Content may be shared for personal, non-commercial use with proper
          attribution. Republication requires written permission.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          6. Limitation of liability
        </p>
        <p>
          Vexent Wire, its editors, and contributors shall not be liable for any direct, indirect, incidental,
          consequential, or punitive damages arising from any user's use of, or reliance on, content on this site.
        </p>
        <p className="text-[13px] text-neutral-600 pt-3">
          These Terms of Service are subject to review and update. Continued use of the site following any
          changes constitutes acceptance of the revised Terms.
        </p>
      </>
    ),
  },
  billing: {
    eyebrow: "Legal",
    title: "Billing & Refunds",
    body: (
      <>
        <p>
          Thank you for supporting independent research. This page explains how our paid plans are billed,
          how the free trial works, and our refund policy — in plain language, up front, so there are no
          surprises.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Free tier — always free
        </p>
        <p>
          Reading the daily feed and market wire is and will remain free. No credit card is required to browse
          the site.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Premium &amp; Professional — 30-day free trial
        </p>
        <p>
          Both paid plans begin with a 30-day free trial. When you sign up, you'll create an account with your
          email and enter a valid payment method. Your card is authorized but is not charged during the trial.
        </p>
        <p>
          If you do not cancel before the trial ends, your card will be automatically charged at the plan's
          selected rate — $12/month or $50/month, or the annual rate if you selected an annual plan — on the
          day the trial ends. Your subscription will then renew automatically at the same interval and rate on
          each renewal date until you cancel.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Automatic renewal
        </p>
        <p>
          Paid subscriptions renew automatically. You'll receive an email receipt every time your card is
          charged. Prices displayed at signup are the prices you will be charged; we will not change your
          renewal price without notifying you by email in advance.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          How to cancel
        </p>
        <p>
          You can cancel your subscription at any time from your account settings or from the Stripe-hosted
          billing portal linked from your profile. Cancellation is immediate — no future charges will be made
          — and your access continues through the end of the current paid period. During the free trial,
          cancelling before the 30-day mark means you will never be charged.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Refund policy
        </p>
        <p>
          We're an independent research publication, and every subscription helps fund the work. To keep our
          rates fair and our operations sustainable, all paid charges are non-refundable once processed.
          That said, we care about doing right by our readers — if you were charged in error, believe there
          has been a billing mistake, or are dealing with unusual circumstances, please reach out to us at{" "}
          <span className="font-mono-tick">billing@vexentnews.com</span> and we'll review your case
          promptly and in good faith.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Payment processing
        </p>
        <p>
          All payments are processed securely by Stripe, an industry-leading payment provider. Vexent Wire
          does not store or have access to your full card number. Your billing information is protected under
          Stripe's PCI-DSS Level 1 compliance.
        </p>

        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Questions?
        </p>
        <p>
          If anything about your bill is unclear, we'd rather hear from you than have you be surprised.
          Email <span className="font-mono-tick">billing@vexentnews.com</span> and a real person will
          reply. Thank you for reading Vexent Wire.
        </p>
      </>
    ),
  },
  comments: {
    eyebrow: "Legal",
    title: "Comment Policy",
    body: (
      <>
        <p>
          Vexent Wire welcomes thoughtful discussion. Comments are moderated to keep the discussion useful for
          all readers.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          What is not allowed
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Personal attacks, harassment, or targeted abuse of readers, editors, or public figures</li>
          <li>Racial, ethnic, religious, or gender-based slurs</li>
          <li>Threats of violence or incitement to violence</li>
          <li>Spam, promotional content, or affiliate-link posting</li>
          <li>Coordinated posting, sockpuppeting, or use of automated accounts</li>
          <li>Direct impersonation of Vexent Wire editorial or contributors</li>
        </ul>
        <p className="pt-2">
          Comments that violate this policy will be removed. Repeated violations will result in a permanent ban.
          Moderators may act at their discretion and are not obligated to explain individual removals.
        </p>
      </>
    ),
  },
  dmca: {
    eyebrow: "Legal",
    title: "Copyright & DMCA",
    body: (
      <>
        <p>
          Vexent Wire respects intellectual property rights and complies with the Digital Millennium Copyright Act.
        </p>
        <p className="font-condensed uppercase tracking-widest text-[13px] text-[#0B1F33] font-bold pt-2">
          Filing a DMCA notice
        </p>
        <p>
          If you believe content on this site infringes your copyright, send a written notice to our designated
          agent at <span className="font-mono-tick">dmca@vexentnews.com</span> containing:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>A physical or electronic signature of the copyright owner or authorized agent</li>
          <li>Identification of the copyrighted work claimed to be infringed</li>
          <li>The URL(s) on this site of the allegedly infringing material</li>
          <li>Your contact information (name, address, phone, email)</li>
          <li>A statement of good-faith belief that the disputed use is not authorized</li>
          <li>A statement, under penalty of perjury, that the notification is accurate and you are the copyright owner or authorized agent</li>
        </ul>
        <p className="text-[13px] text-neutral-600 pt-3">
          Full DMCA procedures to be finalized with counsel.
        </p>
      </>
    ),
  },
};

export const LegalPage = () => {
  const { section } = useParams();
  const key = (section || "terms").toLowerCase();
  const c = CONTENT[key] || CONTENT.terms;
  return (
    <PlaceholderPage
      eyebrow={c.eyebrow}
      title={c.title}
      body={c.body}
      testid={`legal-${key}`}
    />
  );
};

export default LegalPage;
